<script setup lang="ts">
import { computed } from 'vue';

import type { CalendarEvent } from '@/api';
import { datetimeStringToTimestamp, timestampToDatetimeString } from '@/utils/datetimeUtils';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import {
  getObjectCriterionState,
  getFieldsForRule,
} from '@/utils/contractManagementView/staticFilters';
import {
  CRITERION_TYPES,
  type CriterionItem,
  type CriterionTypeValue,
} from '@/utils/contractManagementView/criterionConstants';

interface Props {
  rule: CriterionItem;
  path: number[];
}

interface Emits {
  (
    e: 'update-object-field',
    path: number[],
    field: string,
    operator: CriterionTypeValue,
    value: string | number
  ): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Recurrence type options (values from CalendarEvent.recurrence_type union type in API)
type RecurrenceType = NonNullable<CalendarEvent['recurrence_type']>;
const RECURRENCE_TYPE_OPTIONS: { label: string; value: RecurrenceType }[] = [
  { label: 'Infinite', value: 'infinite' },
  { label: 'Count', value: 'count' },
  { label: 'Until Date', value: 'until_date' },
  { label: 'Never', value: 'never' },
];

// Values match how getObjectCriterionState reads a BoolCriterion back.
const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

// Get current state of the rule
const ruleState = computed(() => getObjectCriterionState(props.rule));

/** `kind` of the selected field — set on static-column date and boolean fields. */
const fieldKind = computed(
  () => getFieldsForRule(props.rule).find(f => f.key === ruleState.value?.field)?.kind
);

// Get available fields for this rule
const availableFields = computed(() => {
  return getFieldsForRule(props.rule).map((f: { key: string; label: string }) => ({
    label: f.label,
    value: f.key,
  }));
});

// Get available operators for the current field
const availableOperators = computed(() => {
  const field = ruleState.value?.field ?? '';
  return getFieldOperators(props.rule, field);
});

// A static-column date (start / end date) is a calendar day, not a timestamp
const isDateField = computed(() => fieldKind.value === 'date');
const isBooleanField = computed(() => fieldKind.value === 'boolean');

// Get placeholder text based on field
const placeholder = computed(() => {
  const field = ruleState.value?.field;
  if (field === 'amount') return 'Amount';
  if (field === 'recurrence_count') return 'Count (number)';
  if (field === 'recurrence_interval') return 'Interval (number)';
  if (field === 'recurrence_frequency') return 'Frequency (e.g., day, week, month)';
  return 'Value';
});

// Check if current field is a datetime field
const isDatetimeField = computed(() => {
  const field = ruleState.value?.field;
  return field === 'event_date' || field === 'recurrence_until_date';
});

// Check if current field is recurrence_type
const isRecurrenceTypeField = computed(() => {
  return ruleState.value?.field === 'recurrence_type';
});

// Handle field change
function handleFieldChange(field: string): void {
  const state = ruleState.value;
  const ops = getFieldOperators(props.rule, field);
  // Default to first operator if current one is not valid for new field
  const currentOperator = state?.operator;
  const nextOp =
    currentOperator !== undefined && ops.some(o => o.value === currentOperator)
      ? currentOperator
      : ops[0].value;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
  emit('update-object-field', props.path, field, nextOp, state?.value || '');
}

// Handle operator change
function handleOperatorChange(operator: CriterionTypeValue): void {
  const state = ruleState.value;
  if (state) {
    emit('update-object-field', props.path, state.field, operator, state.value);
  }
}

function handleSelectValueChange(val: string | number | null): void {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
  handleValueChange(val || '');
}

// Handle value change
function handleValueChange(value: string | number): void {
  const state = ruleState.value;
  if (state) {
    emit('update-object-field', props.path, state.field, state.operator, value);
  }
}

// Handle datetime value change
function handleDatetimeChange(timestamp: number | null): void {
  const state = ruleState.value;
  if (state) {
    emit(
      'update-object-field',
      props.path,
      state.field,
      state.operator,
      timestampToDatetimeString(timestamp)
    );
  }
}

// Get available operators for a specific field within a complex type
function getFieldOperators(
  rule: CriterionItem,
  fieldKey: string
): { label: string; value: CriterionTypeValue }[] {
  const kind = getFieldsForRule(rule).find(f => f.key === fieldKey)?.kind;
  // Fields that support comparison operators (numeric, date/time)
  const comparisonFields = [
    'amount', // CurrencyAmount
    'event_date', // CalendarEvent - TimeCriterion
    'recurrence_count', // CalendarEvent - NumberCriterion
    'recurrence_until_date', // CalendarEvent - TimeCriterion
    'recurrence_interval', // CalendarEvent - NumberCriterion
  ];
  const isComparisonField = comparisonFields.includes(fieldKey) || kind === 'date';

  if (isComparisonField) {
    return [
      { label: 'Equals', value: CRITERION_TYPES.EQUALS },
      { label: 'Greater Than', value: CRITERION_TYPES.GREATER_THAN },
      { label: 'Greater or Equal', value: CRITERION_TYPES.GREATER_EQUAL },
      { label: 'Less Than', value: CRITERION_TYPES.LESS_THAN },
      { label: 'Less or Equal', value: CRITERION_TYPES.LESS_EQUAL },
    ];
  }

  // Enum fields (recurrence_type) - only support Equals since it's an enum
  if (fieldKey === 'recurrence_type' || kind === 'boolean') {
    return [{ label: 'Equals', value: CRITERION_TYPES.EQUALS }];
  }

  // String fields (support Contains and Equals)
  return [
    { label: 'Contains', value: CRITERION_TYPES.CONTAINS },
    { label: 'Equals', value: CRITERION_TYPES.EQUALS },
  ];
}
</script>

<template>
  <div style="display: flex; gap: 8px; flex: 1; min-width: 350px">
    <!-- 1. Field Selector -->
    <gaia-select
      :value="ruleState?.field"
      :options="availableFields"
      placeholder="Field"
      size="small"
      style="flex: 2"
      @update:value="handleFieldChange"
    />

    <!-- 2. Operator Selector -->
    <gaia-select
      :value="ruleState?.operator"
      :options="availableOperators"
      placeholder="Op"
      size="small"
      style="flex: 1.5; min-width: 80px"
      @update:value="handleOperatorChange"
    />

    <!-- 3. Value Input -->
    <!-- Datetime field: Use NDatePicker -->
    <gaia-date-picker
      v-if="isDatetimeField"
      :value="datetimeStringToTimestamp(ruleState?.value as string | number | null)"
      type="datetime"
      placeholder="Select date and time"
      size="small"
      style="flex: 3"
      clearable
      format="yyyy-MM-dd HH:mm:ss"
      @update:value="handleDatetimeChange"
    />
    <!-- Static-column date: calendar day, sent as YYYY-MM-DD -->
    <gaia-date-picker
      v-else-if="isDateField"
      :formatted-value="(ruleState?.value as string) || null"
      value-format="yyyy-MM-dd"
      type="date"
      placeholder="Select date"
      size="small"
      style="flex: 3"
      clearable
      @update:formatted-value="(value: string | null) => handleValueChange(value ?? '')"
    />
    <!-- Boolean field: yes / no -->
    <gaia-select
      v-else-if="isBooleanField"
      :value="ruleState ? String(ruleState.value) : null"
      :options="BOOLEAN_OPTIONS"
      placeholder="Yes / No"
      size="small"
      style="flex: 3"
      @update:value="handleSelectValueChange"
    />
    <!-- Recurrence type: Use select with enum values -->
    <gaia-select
      v-else-if="isRecurrenceTypeField"
      :value="(ruleState?.value as string) || null"
      :options="RECURRENCE_TYPE_OPTIONS"
      placeholder="Select recurrence type"
      size="small"
      style="flex: 3"
      clearable
      @update:value="handleSelectValueChange"
    />
    <!-- Other fields: Use regular input -->
    <gaia-input
      v-else
      :value="String(ruleState?.value ?? '')"
      type="text"
      :placeholder="placeholder"
      size="small"
      style="flex: 3"
      @update:value="handleValueChange"
    />
  </div>
</template>
